import axios from "axios";
import { handleError } from "../utils/helper";

const CLAIMSPRO_HOST_URL = process.env.REACT_APP_CLAIMSPRO_HOST_URL;

const getClaimReasons = async () => {
  try {
    return (await axios.get(`${CLAIMSPRO_HOST_URL}/reports/reasons`)).data;
  } catch (error) {
    return handleError(error);
  }
};

/**
 * Retrieves claims from the server based on the provided claimId.
 * @async
 * @param {Object} options - The options for retrieving claims.
 * @param {string} options.claimId - The claimId used to filter the claims. Optional.
 * @returns {Promise<Object>} A promise that resolves to the claims data.
 */
const getClaims = async ({ claimId, lpn }) => {
  try {
    if (lpn) {
      return (await axios.get(`${CLAIMSPRO_HOST_URL}/reports?lpn=${lpn}`)).data;
    } else {
      return (await axios.get(`${CLAIMSPRO_HOST_URL}/reports/${claimId}`)).data;
    }
  } catch (error) {
    return handleError(error);
  }
};

/**
 * Creates a claim.
 *
 * @async
 * @param {Object} data - The data for the claim update.
 * @param {number} data.claimReasonId - The ID of the claim reason.
 * @param {number} data.claimSubReasonId = the ID of the claim sub reason.
 * @param {string=} data.claimReasonNote - The note pertaining to a claim sub reason.
 * @param {string} data.lpn - The LPN (license plate number) for the claim.
 * @param {number} data.userId - The ID of the user associated with the claim.
 * @param {Array} data.claimImages - An array of claim images.
 * @param {string} data.claimImages[].imageSrc - The source of the claim image.
 * @param {string} data.claimImages[].imageId - The ID of the claim image.
 * @returns {Promise<Object>} A promise that resolves to the updated claim data.
 */
const createClaim = async (data) => {
  try {
    return (await axios.post(`${CLAIMSPRO_HOST_URL}/reports`, data)).data;
  } catch (error) {
    return handleError(error);
  }
};

/**
 * Updates a claim.
 * @async
 * @param {number} claimId - The ID of the claim to update.
 * @param {Object} data - The data for the claim update.
 * @param {number} data.claimReasonId - The ID of the claim reason (integer).
 * @param {Array} data.claimImages - An array of claim images.
 * @param {string} data.claimImages[].imageSrc - The source of the claim image (string).
 * @param {string} data.claimImages[].imageId - The ID of the claim image (string).
 * @returns {Promise<Object>} A promise that resolves to the updated claim data.
 */
const updateClaim = async (claimId, data) => {
  try {
    return (await axios.patch(`${CLAIMSPRO_HOST_URL}/reports/${claimId}`, data))
      .data;
  } catch (error) {
    return handleError(error);
  }
};

const getClaimImages = async (claimId) => {
  try {
    return (await axios.get(`${CLAIMSPRO_HOST_URL}/reports/images/${claimId}`))
      .data;
  } catch (error) {
    return handleError(error);
  }
};

const getRemovalShipmentDetails = async (lpn) => {
  try {
    return (await axios.get(`${CLAIMSPRO_HOST_URL}/reports/details/${lpn}`))
      .data;
  } catch (error) {
    return handleError(error);
  }
};

const services = {
  getClaimReasons,
  getClaims,
  createClaim,
  updateClaim,
  getClaimImages,
  getRemovalShipmentDetails,
};

export default services;
